import React from 'react';
import DemoSearch from './demo';
import { mountWidgetInShadowHost } from './shadow_mount';
import {
  initDemoEmbed,
  readDemoEmbedConfig,
  bringDemoUp,
  cancelPendingDemoMount,
  createDemoTokenResolver,
  cancelDemoHealthWatch,
  registerDemoHalt,
  unregisterDemoHalt,
  revealDemoLive,
} from './demo-embed';
import { createDemoTransport, DEMO_HALT } from './demo-transport';
import { themePinUi, themeChoiceUi, lookChoiceUi, lookUi, presetUi, presetShape, resultPartsUi, resultPartsChoiceUi } from './demo-theme';
import { resolveBaseUrl } from './hooks/use_base_url';
import cssText from './styles/vo-search.css';

/**
 * Entry point for the embedded search demo bundle (item #7970).
 *
 * Built to static/search/v5/vo-search-demo.js by scripts/build-search.js.
 * The VO landing page loads it next to a
 * `<div data-vo-search-demo data-demo-index="...">` container (or a
 * `data-public-token` one; see readDemoEmbedConfig) and gets the
 * full DemoSearch behaviour: fixture-mode autoplay with zero network calls
 * (#7964), and session-rate-limited real searches against the demo instance
 * after explicit user interaction (#7970).
 *
 * This bundle is deliberately separate from the customer-facing vo-search.js
 * so customer embeds never carry the fixture dataset or autoplay code.
 */

/**
 * The ui a container's pin asks for: its look (in its pinned accent, item
 * i22219), else its theme, else nothing.
 *
 * @param {{look?: string|null, accent?: string|null, theme?: string|null}} config
 * @returns {Object}
 */
function pinnedUi(config) {
  if (config.look) return config.accent ? lookChoiceUi(config.look, config.accent) : lookUi(config.look);
  return config.theme ? themePinUi(config.theme) : {};
}

/**
 * The preset a container shows (item i22491): the one chosen at runtime, else
 * the one it pins.
 *
 * @param {{preset?: string|null}} config
 * @returns {string|null}
 */
function activePreset(config) {
  return chosenPreset !== undefined ? chosenPreset : config.preset ?? null;
}

/**
 * The shape a container mounts in: its preset's, else its own pin.
 *
 * @param {{shape: Object|null, preset?: string|null}} config
 * @returns {Object|null}
 */
function mountShape(config) {
  return presetShape(activePreset(config)) ?? config.shape;
}

/**
 * The ui a container renders with now: the chosen look or theme (else its
 * pin), with its preset layered over it, then its result-parts pin and the
 * parts the visitor switched.
 *
 * @param {Object} config
 * @returns {Object}
 */
function currentUi(config) {
  return {
    ...(chosenThemeUi || pinnedUi(config)),
    ...presetUi(activePreset(config)),
    // The parts a result shows are the page's call, then the visitor's
    // (item i22906): the landing pins all four over any preset.
    ...resultPartsUi(config.resultParts),
    ...chosenPartsUi,
  };
}

/**
 * Mount the DemoSearch React tree in the demo container's shadow root.
 *
 * Through the SAME mounter a customer embed uses (item #8774). The demo used to
 * call createRoot on the container itself, in the light DOM, while every
 * customer mounts in a shadow root -- and decisions #5050 and #5069 both name
 * that split as the reason four rounds of shadow-root style defects shipped:
 * "the one verification surface was structurally incapable of exposing the
 * defect." The demo is now a customer, so it can see them.
 *
 * What stays split is the BUNDLE: autoplay, the screenshot health watch and the
 * rate-limit pause must never reach a customer's page, which is why the render
 * callback returns DemoSearch rather than the widget directly.
 *
 * The shape and overlay scope are per container (item #8686): four containers
 * on one page render four different shapes from the one demo instance, which is
 * what makes the landing page demonstrate what a placement can be rather than
 * showing the same widget four times.
 *
 * A scoped overlay portals into the root the widget renders in and lays its box
 * out against it (items #8677/#8711). That containing block is now the shared
 * mounter's mount point rather than the page's own container element.
 *
 * @param {Element} container - The [data-vo-search-demo] element
 * @param {{clientPublicToken: string, shape: Object|null, overlayScope: string}} config -
 *   Resolved embed config
 */
function mountDemo(container, config) {
  // Built here rather than inside DemoSearch because this is the only place
  // that knows which CONTAINER the demo belongs to, and a fallback swap has to
  // halt the demo it just hid without touching the one the visitor is looking
  // at (item #9013).
  const transport = createDemoTransport();
  registerDemoHalt(container, () => transport.emit(DEMO_HALT));

  // A panel that showed its screenshot has to stop showing it now (item #9014),
  // or this widget mounts behind a photograph of itself. Done here rather than
  // in the request path alone so every mount says the same thing: a demo is
  // about to be alive in this slot.
  revealDemoLive(document, container);

  let portal = null;
  const shape = mountShape(config);
  const element = (themeUi) => (
    <DemoSearch
      clientPublicToken={config.clientPublicToken}
      demoMode={true}
      demoTransport={transport}
      shapeOverride={shape}
      autoplayPace={config.autoplayPace ?? null}
      autoplayStart={config.autoplayStart ?? null}
      portalTarget={portal}
      ui={{
        // The render mode (inline on the landing page) is authoritative
        // per-instance server config (#5414), set on the demo instance via the
        // config endpoint / dashboard -- the embed consumes it and never forces
        // a client-side mode. Only demo behavior is overridden here:
        // pushState/autoFocus must be repeated even though DemoSearch defaults
        // them, because its `{...props}` spread comes after its ui merge, so
        // this object REPLACES those defaults rather than extending them. A
        // landing-page embed must never rewrite the URL or steal focus.
        pushState: false,
        autoFocus: false,
        // The demo shows what a paid account can set: no "Search by Visiting
        // Objects" footer. Free accounts keep it; the customer bundle is
        // untouched (owner call, 2026-10-15).
        hideAttribution: true,
        ...themeUi,
      }}
    />
  );

  // A pinned look (item i22185) or theme (item i21806) is shown as designed; a
  // theme the visitor chose at runtime outlives a tab switch (item i21807);
  // unpinned, the server's theme decides as before. A preset layers over it.
  const initialUi = currentUi(config);
  const mounted = mountWidgetInShadowHost(container, {
    overlayScope: config.overlayScope,
    render: ({ portalTarget }) => {
      portal = portalTarget;
      return element(initialUi);
    },
  });
  roots.set(container, mounted.root);
  mountedShapes.set(container, shapeKey(shape));
  configs.set(container, config);
  // Re-rendering the same root with the same transport restyles the widget in
  // place: React keeps its state, so autoplay, the typed query and an open
  // popup all survive (item i21807).
  restylers.set(container, () => mounted.root.render(element(currentUi(config))));
  return mounted;
}

/** Re-render functions for each mounted demo container (item i21807). */
const restylers = new Map();

/** The shape each mounted container rendered in, as a shapeKey. */
const mountedShapes = new Map();

/** The embed config each mounted container was mounted with. */
const configs = new Map();

/** The theme the visitor last chose, applied to demos mounted after it. */
let chosenThemeUi = null;

/**
 * The result parts the visitor switched (item i22906), applied over every
 * demo's own pin, and to demos mounted after.
 */
let chosenPartsUi = {};

/**
 * The preset chosen at runtime (item i22491): undefined until setPreset is
 * called, so a container's own pin decides; null once cleared.
 */
let chosenPreset;

/**
 * A comparable key for a render shape.
 *
 * @param {Object|null} shape
 * @returns {string}
 */
function shapeKey(shape) {
  return shape ? `${shape.trigger}/${shape.surface}/${shape.layout}` : '';
}

/**
 * Restyle the live demo without remounting it (item i21807, doc11089).
 *
 * @param {string} theme - light, dark, vo-light or vo-dark
 * @param {Object} [opts]
 * @param {string} [opts.accent] - an ACCENT_PRESETS key
 * @returns {boolean} false, changing nothing, for an unknown theme or accent
 */
function setTheme(theme, { accent } = {}) {
  return restyleAll(themeChoiceUi(theme, accent));
}

/**
 * Restyle the live demo with a named look (item i22025): a theme, or a custom
 * look built from settings a customer can set. Same in-place re-render as
 * setTheme, so autoplay keeps typing through it.
 *
 * @param {string} look - a DEMO_LOOKS name
 * @param {Object} [opts]
 * @param {string} [opts.accent] - an ACCENT_PRESETS key (item i22186)
 * @returns {boolean} false, changing nothing, for an unknown look or accent
 */
function setLook(look, { accent } = {}) {
  return restyleAll(lookChoiceUi(look, accent));
}

/**
 * Show a named preset on every mounted demo, and on demos mounted after
 * (item i22491, decision d11705). A preset that keeps a container's shape
 * restyles it in place, so autoplay keeps typing; one that changes the shape
 * remounts it, because the shape is read only at mount.
 *
 * @param {string|null} name - a DEMO_PRESETS name, or null to clear
 * @returns {boolean} false, changing nothing, for an unknown preset
 */
function setPreset(name) {
  if (name !== null && !presetUi(name)) return false;
  chosenPreset = name;
  for (const [container, restyle] of [...restylers]) {
    if (shapeKey(mountShape(configs.get(container))) === mountedShapes.get(container)) {
      restyle();
    } else {
      unmountOnRequest(container);
      mountOnRequest(container);
    }
  }
  return true;
}

/**
 * Show or hide parts of each result on every mounted demo, in place, so
 * autoplay keeps typing (item i22906). Parts left out keep their state.
 *
 * @param {Record<string, boolean>} parts - thumbnails, excerpts, icons or
 *   tags, each on or off
 * @returns {boolean} false, changing nothing, for an unknown part or a value
 *   that is not a boolean
 */
function setResultParts(parts) {
  const partsUi = resultPartsChoiceUi(parts);
  if (!partsUi) return false;
  chosenPartsUi = { ...chosenPartsUi, ...partsUi };
  for (const restyle of restylers.values()) restyle();
  return true;
}

/**
 * Apply ui overrides to every mounted demo, and to demos mounted after.
 *
 * @param {Object|null} themeUi
 * @returns {boolean} false for no overrides
 */
function restyleAll(themeUi) {
  if (!themeUi) return false;
  chosenThemeUi = themeUi;
  for (const restyle of restylers.values()) restyle();
  return true;
}

/**
 * The React root behind each mounted demo container (item #8854).
 *
 * The landing's tabbed demo keeps exactly ONE live widget: #4968 bounds the
 * demo instance to roughly five concurrent viewers, and four autoplaying demos
 * would divide that by four for a visitor looking at one tab. The page decides
 * which tab is live; the bundle does the mounting, because mounting a widget is
 * the bundle's job and nobody else has the tree.
 */
const roots = new WeakMap();

/**
 * Turns a container's data-demo-index into the CURRENT public token of the
 * environment this bundle is served from (item #17619, decision #6093). The
 * origin is the same one the mounted widget will query, so the token it gets
 * is the one that origin's search-ingest will accept. A token baked into the
 * page at build time is one environment's mint; this is how the page ships
 * the mapping instead.
 */
const resolveToken = createDemoTokenResolver({
  fetch: (...args) => fetch(...args),
  baseUrl: resolveBaseUrl(typeof window !== 'undefined' ? window.location : undefined),
});

/**
 * Mount a demo container the page has decided to bring to life.
 *
 * Reads the container's own config, so a deferred tab renders in the shape IT
 * pins rather than the first container's (item #8686). Mounting an already
 * mounted container is a no-op.
 *
 * It is watched for health exactly like a bootstrapped one (item #8860): the
 * tabs mount three of their four demos this way, so an unwatched request path
 * would leave three modes with a fallback still nothing ever reveals.
 *
 * A container naming its instance by id mounts once its token resolves
 * (item #17619); the call returns true as soon as that resolution is under
 * way, and a second request while it is pending is refused rather than
 * started twice.
 *
 * @param {Element} container - an element carrying the demo config attributes
 * @param {Object} [opts]
 * @param {function(Function, number): *} [opts.schedule] - health-check timer
 * @returns {boolean} True when this call mounted it or started resolving it
 */
function mountOnRequest(container, { schedule } = {}) {
  if (!container || roots.get(container)) return false;
  const config = readDemoEmbedConfig(container);
  if (!config) return false;
  return bringDemoUp(document, { container, config, mount: mountDemo, resolveToken, schedule });
}

/**
 * Tear a demo down, leaving its container ready to mount again.
 *
 * Unmounting rather than halting is deliberate: it drops the SearchInput's
 * subscription, which is what stops autoplay (item #8842), and leaves nothing
 * marked as taken over -- so a tab the visitor comes back to plays again
 * instead of sitting dead for the rest of the page's life.
 *
 * The demo's health deadline goes with it (item #9012). Left running, it fires
 * against the shadow root this just emptied, finds no widget, and shows the
 * failure photograph for a demo whose only fault was not being selected any
 * more -- and its swap halts the demo the visitor moved TO.
 *
 * @param {Element} container - a previously mounted container
 * @returns {boolean} True when this call unmounted it
 */
function unmountOnRequest(container) {
  // A demo still waiting on its token has no tree yet; dropping the
  // resolution is the whole unmount (item #17619).
  if (cancelPendingDemoMount(container)) return true;
  const root = container && roots.get(container);
  if (!root) return false;
  cancelDemoHealthWatch(container);
  // Nothing to halt once the tree is gone, and the next mount registers its own
  // transport (item #9013).
  unregisterDemoHalt(container);
  root.unmount();
  roots.delete(container);
  restylers.delete(container);
  mountedShapes.delete(container);
  configs.delete(container);
  return true;
}

/**
 * Marker for a bootstrap that already ran, on window rather than in module
 * scope: a classic script executes once per TAG, so two tags produce two
 * independent copies of this module and a module-level flag would not be shared
 * between them.
 */
const BOOTSTRAPPED = '__voSearchDemoBootstrapped';

/**
 * Bootstrap every demo on the page, once.
 *
 * A page can carry more than one component that wants this bundle -- the /v5
 * search showcase and the display-mode band both do -- and each emits its own
 * script tag. Without the guard the second execution calls createRoot on
 * containers React already owns.
 *
 * @returns {boolean} True when this call did the mounting
 */
export function initDemoStandalone() {
  if (typeof window !== 'undefined') {
    if (window[BOOTSTRAPPED]) return false;
    window[BOOTSTRAPPED] = true;
  }

  const mounted = initDemoEmbed(document, { mount: mountDemo, cssText, resolveToken });

  // The page can ask for a container the bootstrap deliberately left alone (a
  // tab that is not selected). Announced with an event because the bundle loads
  // async: a tab clicked before it lands has nothing to call, so the page waits
  // for this rather than polling.
  window.voSearchDemo = {
    mount: mountOnRequest,
    unmount: unmountOnRequest,
    setTheme,
    setLook,
    setPreset,
    setResultParts,
    /** Test seam: the React root behind a container. */
    __rootFor: (container) => roots.get(container),
  };
  window.dispatchEvent(new CustomEvent('vo-search-demo-ready'));

  if (!mounted) {
    console.error(
      '[vo-search-demo] No configured demo container found. Expected:',
    );
    console.error('  <div data-vo-search-demo data-demo-index="..."></div>');
    console.error('  or <div data-vo-search-demo data-public-token="..."></div>');
  }
  return true;
}

const init = initDemoStandalone;

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
